import React from 'react';
import { useQuery } from '@apollo/client';
import { Trash2, GripVertical, Building2, Plane, Bus, FileText } from 'lucide-react';
import { GET_AIRLINES } from '../../graphql/queries/airline';
import HotelSearchInput, { type PlaceResult } from './HotelSearchInput';

const HOTEL_STARS_OPTIONS = ['1*', '2*', '3*', '4*', '5*'];
const ROOM_TYPE_OPTIONS = ['Single', 'Double', 'Triple', 'Quadruple', 'Quintuple', 'Suite'];
const MEAL_PLAN_OPTIONS = [
  'Sans repas',
  'Petit-déjeuner',
  'Demi-pension',
  'Pension complète',
  'All inclusive',
];
const FLIGHT_CLASS_OPTIONS = ['Économique', 'Premium', 'Affaires', 'Première'];
const VEHICLE_TYPE_OPTIONS = ['Bus', 'Minibus', 'Van', 'Voiture', 'Taxi', 'Navette'];
const ROOM_COUNT_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1);

export type QuoteLineItemType = 'HOTEL' | 'FLIGHT' | 'TRANSFER' | 'CUSTOM';

export interface LineItemDraft {
  id?: number;
  type: QuoteLineItemType;
  position: number;
  title: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  total: number;

  // hotel
  hotelName?: string;
  hotelLocation?: string;
  hotelStars?: string;
  checkIn?: string;
  checkOut?: string;
  numberOfNights?: number;
  numberOfRooms?: number;
  roomType?: string;
  mealPlan?: string;

  // flight
  airline?: string;
  flightFrom?: string;
  flightTo?: string;
  departureDate?: string;
  returnDate?: string;
  flightClass?: string;
  flightNumber?: string;

  // transfer
  transferFrom?: string;
  transferTo?: string;
  transferDate?: string;
  vehicleType?: string;

  providerSource?: string;
  providerRefId?: string;
  providerData?: any;
}

interface Props {
  item: LineItemDraft;
  onChange: (next: LineItemDraft) => void;
  onRemove: () => void;
}

const TYPE_META: Record<QuoteLineItemType, { label: string; Icon: any; color: string }> = {
  HOTEL: { label: 'Hôtel', Icon: Building2, color: 'text-purple-600' },
  FLIGHT: { label: 'Vol', Icon: Plane, color: 'text-blue-600' },
  TRANSFER: { label: 'Transfert', Icon: Bus, color: 'text-orange-600' },
  CUSTOM: { label: 'Prestation', Icon: FileText, color: 'text-gray-600' },
};

export default function QuoteLineItemEditor({ item, onChange, onRemove }: Props) {
  const { Icon, label, color } = TYPE_META[item.type];

  const { data: airlinesData } = useQuery(GET_AIRLINES, {
    skip: item.type !== 'FLIGHT',
  });
  const airlineOptions = (airlinesData?.airlines || [])
    .filter((a: any) => a.isActive !== false)
    .map((a: any) => ({ value: a.name, label: a.code ? `${a.name} (${a.code})` : a.name }));

  const patch = (p: Partial<LineItemDraft>) => {
    const next = { ...item, ...p };
    // recompute total when qty/unit changes (unless edited explicitly)
    const qty = Number(next.quantity) || 1;
    const unit = Number(next.unitPrice) || 0;
    if (p.quantity !== undefined || p.unitPrice !== undefined) {
      next.total = Math.round(qty * unit * 100) / 100;
    }
    // auto-compute nights if hotel & checkIn/checkOut both set
    if (next.type === 'HOTEL' && next.checkIn && next.checkOut) {
      try {
        const ci = new Date(next.checkIn).getTime();
        const co = new Date(next.checkOut).getTime();
        if (co > ci) {
          const nights = Math.round((co - ci) / (24 * 3600 * 1000));
          next.numberOfNights = nights;
        }
      } catch {
        /* ignore */
      }
    }
    onChange(next);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-start gap-3 mb-3">
        <GripVertical className="w-5 h-5 text-gray-300 mt-2 cursor-move" />
        <div className={`${color} mt-1`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className={`text-xs font-medium uppercase tracking-wider ${color}`}>
              {label}
            </span>
            <button
              onClick={onRemove}
              className="text-red-500 hover:text-red-700 p-1"
              title="Supprimer cette ligne"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <input
            type="text"
            value={item.title}
            onChange={(e) => patch({ title: e.target.value })}
            placeholder="Titre (ex: Hôtel Sheraton Alger, Vol AH1234, Transfert aéroport)"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium"
          />
        </div>
      </div>

      {/* Recherche rapide d'hôtel — visible uniquement pour les lignes HOTEL */}
      {item.type === 'HOTEL' && (
        <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50/40 p-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-blue-700 mb-1.5">
            Recherche rapide
          </div>
          <HotelSearchInput
            placeholder="Rechercher un hôtel par nom (ex: Sheraton Alger)…"
            onSelect={(place: PlaceResult) => {
              const cityCountry = [place.city, place.country].filter(Boolean).join(', ');
              patch({
                title: item.title?.trim() ? item.title : `Hôtel ${place.name}`,
                hotelName: place.name,
                hotelLocation: cityCountry || place.address || undefined,
                hotelStars: place.stars ? `${place.stars}*` : item.hotelStars,
                providerSource: place.source || 'manual',
                providerRefId: place.id || undefined,
                // Stocke image et website dans providerData (JSON) sans changer le schéma
                providerData: {
                  ...(item as any).providerData,
                  image: place.image || (item as any).providerData?.image || null,
                  website: place.website || (item as any).providerData?.website || null,
                  address: place.address || null,
                  latitude: place.latitude || null,
                  longitude: place.longitude || null,
                },
              });
            }}
          />
        </div>
      )}

      {/* Champs spécifiques au type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        {item.type === 'HOTEL' && (
          <>
            <Field label="Nom de l'hôtel" value={item.hotelName || ''} onChange={(v) => patch({ hotelName: v })} />
            <Field label="Ville / Localisation" value={item.hotelLocation || ''} onChange={(v) => patch({ hotelLocation: v })} />
            <SelectField label="Étoiles" value={item.hotelStars} options={HOTEL_STARS_OPTIONS} onChange={(v) => patch({ hotelStars: v || undefined })} />
            <SelectField label="Type de chambre" value={item.roomType} options={ROOM_TYPE_OPTIONS} onChange={(v) => patch({ roomType: v || undefined })} />
            <SelectField label="Régime" value={item.mealPlan} options={MEAL_PLAN_OPTIONS} onChange={(v) => patch({ mealPlan: v || undefined })} />
            <SelectField label="Nombre de chambres" value={item.numberOfRooms} options={ROOM_COUNT_OPTIONS} onChange={(v) => patch({ numberOfRooms: v ? Number(v) : undefined })} />
            <Field label="Check-in" type="date" value={item.checkIn?.slice(0, 10) || ''} onChange={(v) => patch({ checkIn: v })} />
            <Field label="Check-out" type="date" value={item.checkOut?.slice(0, 10) || ''} onChange={(v) => patch({ checkOut: v })} />
            <Field label="Nombre de nuits" type="number" value={item.numberOfNights ?? ''} onChange={(v) => patch({ numberOfNights: v ? Number(v) : undefined })} />
          </>
        )}

        {item.type === 'FLIGHT' && (
          <>
            <SelectField
              label="Compagnie aérienne"
              value={item.airline}
              options={airlineOptions}
              onChange={(v) => patch({ airline: v || undefined })}
              placeholder={airlineOptions.length === 0 ? 'Aucune compagnie configurée' : '— Sélectionner —'}
            />
            <Field label="N° de vol" placeholder="AH1234" value={item.flightNumber || ''} onChange={(v) => patch({ flightNumber: v })} />
            <Field label="Départ" placeholder="Alger (ALG)" value={item.flightFrom || ''} onChange={(v) => patch({ flightFrom: v })} />
            <Field label="Arrivée" placeholder="Istanbul (IST)" value={item.flightTo || ''} onChange={(v) => patch({ flightTo: v })} />
            <Field label="Date aller" type="date" value={item.departureDate?.slice(0, 10) || ''} onChange={(v) => patch({ departureDate: v })} />
            <Field label="Date retour" type="date" value={item.returnDate?.slice(0, 10) || ''} onChange={(v) => patch({ returnDate: v })} />
            <SelectField label="Classe" value={item.flightClass} options={FLIGHT_CLASS_OPTIONS} onChange={(v) => patch({ flightClass: v || undefined })} />
          </>
        )}

        {item.type === 'TRANSFER' && (
          <>
            <Field label="Départ" value={item.transferFrom || ''} onChange={(v) => patch({ transferFrom: v })} />
            <Field label="Arrivée" value={item.transferTo || ''} onChange={(v) => patch({ transferTo: v })} />
            <Field label="Date" type="date" value={item.transferDate?.slice(0, 10) || ''} onChange={(v) => patch({ transferDate: v })} />
            <SelectField label="Type de véhicule" value={item.vehicleType} options={VEHICLE_TYPE_OPTIONS} onChange={(v) => patch({ vehicleType: v || undefined })} />
          </>
        )}

        {item.type === 'CUSTOM' && (
          <Field
            label="Description détaillée"
            placeholder="Visa, assurance, excursion, repas..."
            value={item.description || ''}
            onChange={(v) => patch({ description: v })}
            full
          />
        )}
      </div>

      {/* Description libre (sauf CUSTOM qui l'a déjà au-dessus) */}
      {item.type !== 'CUSTOM' && (
        <div className="mb-3">
          <Field
            label="Notes complémentaires"
            value={item.description || ''}
            onChange={(v) => patch({ description: v })}
            full
          />
        </div>
      )}

      {/* Prix */}
      <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-100">
        <Field
          label="Quantité"
          type="number"
          value={item.quantity ?? 1}
          onChange={(v) => patch({ quantity: Math.max(1, Number(v) || 1) })}
        />
        <Field
          label="Prix unitaire"
          type="number"
          value={item.unitPrice ?? 0}
          onChange={(v) => patch({ unitPrice: Math.max(0, Number(v) || 0) })}
        />
        <div>
          <div className="text-xs text-gray-500 mb-1">Total</div>
          <div className="px-3 py-2 rounded-md bg-gray-50 border border-gray-200 text-sm font-semibold text-gray-900">
            {(item.total ?? (item.quantity || 1) * (item.unitPrice || 0)).toLocaleString('fr-FR')}
          </div>
        </div>
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  full?: boolean;
}

function Field({ label, value, onChange, type = 'text', placeholder, full }: FieldProps) {
  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input
        type={type}
        value={value as any}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
      />
    </div>
  );
}

interface SelectFieldProps {
  label: string;
  value: string | number | undefined | null;
  onChange: (v: string) => void;
  options: Array<string | number | { value: string | number; label: string }>;
  placeholder?: string;
}

function SelectField({ label, value, onChange, options, placeholder }: SelectFieldProps) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <select
        value={value == null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
      >
        <option value="">{placeholder || '— Sélectionner —'}</option>
        {options.map((opt) => {
          if (typeof opt === 'object') {
            return (
              <option key={String(opt.value)} value={String(opt.value)}>
                {opt.label}
              </option>
            );
          }
          return (
            <option key={String(opt)} value={String(opt)}>
              {String(opt)}
            </option>
          );
        })}
      </select>
    </div>
  );
}
